import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories, site } from "@/mocks/siteData";

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const navLinks = [
    { label: "首页", to: "/" },
    ...categories.map((c) => ({ label: c.name, to: `/explore/${c.slug}` })),
    { label: "关于我们", to: "/about" },
  ];

  const active = (to: string) =>
    to === "/" ? pathname === "/" : pathname.startsWith(to);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-foreground-950/95 backdrop-blur border-b border-background-50/10"
          : "bg-gradient-to-b from-black/50 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-20 max-w-[1400px] items-center justify-between px-4 md:px-6">
        {/* brand */}
        <Link
          to="/"
          aria-label={`${site.name} 首页`}
          className="group flex items-center gap-2.5 whitespace-nowrap"
        >
          <span className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded bg-primary-500 text-background-50">
            <i className="ri-ancient-pavilion-line text-xl" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-black tracking-[0.18em] text-background-50">
              {site.name}
            </span>
            <span className="mt-1 hidden text-[10px] tracking-[0.32em] text-background-50/70 sm:block">
              历史 · 探秘 · 考据
            </span>
          </span>
        </Link>

        {/* desktop nav */}
        <nav aria-label="主导航" className="hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                active(l.to)
                  ? "text-primary-300"
                  : "text-background-100/85 hover:text-background-50"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href="#subscribe"
            className="ml-3 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-mail-send-line" aria-hidden="true"></i>
            订阅探秘简报
          </a>
        </nav>

        {/* mobile toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "关闭菜单" : "打开菜单"}
          aria-expanded={open}
          className="flex h-10 w-10 items-center justify-center rounded-md text-background-50 lg:hidden"
        >
          <i className={`text-2xl ${open ? "ri-close-line" : "ri-menu-line"}`} aria-hidden="true"></i>
        </button>
      </div>

      {/* mobile panel */}
      {open && (
        <nav
          aria-label="移动端导航"
          className="mx-4 mb-4 rounded-lg border border-background-50/10 bg-foreground-950/95 p-3 lg:hidden"
        >
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`block rounded-md px-4 py-3 text-sm font-medium ${
                active(l.to)
                  ? "bg-primary-500/15 text-primary-300"
                  : "text-background-100 hover:bg-background-50/5"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href="#subscribe"
            className="mt-2 flex items-center justify-center gap-2 rounded-md bg-accent-500 px-4 py-3 text-sm font-semibold text-background-50"
          >
            <i className="ri-mail-send-line" aria-hidden="true"></i>
            订阅探秘简报
          </a>
        </nav>
      )}
    </header>
  );
}